/**
 * Network-quality logic shared by the client and its tests. Everything here
 * is a pure function of its inputs so the thresholds from E6-S3, E6-S6 and
 * E6-S8 can be tested without a browser or a clock.
 */

export interface NetworkSample {
  /** Monotonic time of the sample in milliseconds. */
  at: number;
  /** Estimated available bitrate in kbps; null when the browser reports none. */
  availableKbps: number | null;
  lossPct: number;
  rttMs: number;
}

export type VideoMode = 'normal' | 'speakerOnly' | 'audioOnly';

export const FALLBACK = {
  sampleIntervalMs: 3000,
  speakerOnlyBelowKbps: 150,
  audioOnlyBelowKbps: 60,
  degradeAfterMs: 10_000,
  recoverAfterMs: 20_000,
} as const;

export interface FallbackState {
  /** What the measurements call for. */
  measured: VideoMode;
  /** The user tapped "Try video anyway"; cleared once the network recovers. */
  override: boolean;
  below150Since: number | null;
  below60Since: number | null;
  above60Since: number | null;
  above150Since: number | null;
}

export function initialFallbackState(): FallbackState {
  return {
    measured: 'normal',
    override: false,
    below150Since: null,
    below60Since: null,
    above60Since: null,
    above150Since: null,
  };
}

/** The mode the call should actually run in. */
export function effectiveMode(state: FallbackState): VideoMode {
  return state.override ? 'normal' : state.measured;
}

function since(prev: number | null, holds: boolean, at: number): number | null {
  if (!holds) return null;
  return prev ?? at;
}

function held(start: number | null, at: number, ms: number): boolean {
  return start !== null && at - start >= ms;
}

/**
 * Advances the fallback state machine by one stats sample.
 *
 * Degrading needs the bitrate to stay under a threshold for 10 s; recovering
 * needs it to stay at or above the threshold for 20 s. The different windows
 * are the hysteresis that stops the call flapping between modes.
 */
export function stepFallback(state: FallbackState, sample: NetworkSample): FallbackState {
  const kbps = sample.availableKbps;
  if (kbps === null) return state; // No estimate yet: hold the current mode.

  const at = sample.at;
  const next: FallbackState = {
    ...state,
    below150Since: since(state.below150Since, kbps < FALLBACK.speakerOnlyBelowKbps, at),
    below60Since: since(state.below60Since, kbps < FALLBACK.audioOnlyBelowKbps, at),
    above60Since: since(state.above60Since, kbps >= FALLBACK.audioOnlyBelowKbps, at),
    above150Since: since(state.above150Since, kbps >= FALLBACK.speakerOnlyBelowKbps, at),
  };

  if (held(next.below60Since, at, FALLBACK.degradeAfterMs)) {
    next.measured = 'audioOnly';
  } else if (held(next.above150Since, at, FALLBACK.recoverAfterMs)) {
    next.measured = 'normal';
  } else if (state.measured === 'normal' && held(next.below150Since, at, FALLBACK.degradeAfterMs)) {
    next.measured = 'speakerOnly';
  } else if (state.measured === 'audioOnly' && held(next.above60Since, at, FALLBACK.recoverAfterMs)) {
    next.measured = 'speakerOnly';
  }

  if (next.measured === 'normal') next.override = false;
  return next;
}

export function withOverride(state: FallbackState, override: boolean): FallbackState {
  return { ...state, override: override && state.measured !== 'normal' };
}

export type Quality = 'good' | 'fair' | 'poor';

/**
 * Good / Fair / Poor for the signal-bars icon (E6-S8). The bitrate limits
 * follow what the call needs: under 150 kbps video is restricted, and under
 * about 250 kbps the 360p layer no longer fits. A browser's estimate starts
 * near 300 kbps and only grows as it sends more, so anything at or above
 * that is not, by itself, a sign of a weak network.
 */
export function classifyQuality(sample: Pick<NetworkSample, 'availableKbps' | 'lossPct' | 'rttMs'>): Quality {
  const kbps = sample.availableKbps;
  if ((kbps !== null && kbps < 150) || sample.lossPct > 8 || sample.rttMs > 400) return 'poor';
  if ((kbps !== null && kbps < 250) || sample.lossPct > 3 || sample.rttMs > 200) return 'fair';
  return 'good';
}

/** WebSocket reconnect delays: 1, 2, 4, 8 s, then capped at 10 s (E6-S6). */
export function reconnectDelayMs(attempt: number): number {
  const delay = 1000 * 2 ** Math.max(0, attempt);
  return Math.min(delay, 10_000);
}

export const RECONNECT_GIVE_UP_MS = 60_000;
